import { supabase } from '@/integrations/supabase/client';

export type ScopeLevel = 'tenant' | 'factory' | 'area' | 'line' | 'station';
export type AppPermission = string;
export type StylingRole = 'operator' | 'quality' | 'site_manager';

export interface PermissionLocation {
  factoryId?: string | null;
  areaId?: string | null;
  lineId?: string | null;
  stationId?: string | null;
}

export interface RoleAssignmentWithPermissions {
  id: string;
  user_id: string;
  role_id: string;
  tenant_id: string;
  factory_id: string | null;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  status: string;
  role: {
    id: string;
    name: string;
    role_permissions: Array<{
      permission_definition_id: string | null;
      permission_definitions: {
        code: string;
        applicable_scope_level: ScopeLevel | null;
      } | null;
    }>;
  };
}

const SCOPE_RANK: Record<ScopeLevel, number> = {
  tenant: 0,
  factory: 1,
  area: 2,
  line: 3,
  station: 4,
};

const STYLING_ROLE_PERMISSION_MAP: Record<StylingRole, AppPermission> = {
  operator: 'mod_styling.operator',
  quality: 'mod_styling.quality',
  site_manager: 'mod_styling.site_manager',
};

const STYLING_PERMISSION_CODES = new Set<AppPermission>(Object.values(STYLING_ROLE_PERMISSION_MAP));

export async function fetchApprovedRoleAssignments(userId: string, tenantId: string): Promise<RoleAssignmentWithPermissions[]> {
  const { data, error } = await (((supabase as any)
    .schema('core'))
    .from('user_role_assignments')
    .select(`
      id,
      user_id,
      role_id,
      tenant_id,
      factory_id,
      area_id,
      line_id,
      station_id,
      status,
      role:roles!inner(
        id,
        name,
        role_permissions(
          permission_definition_id,
          permission_definitions(code, applicable_scope_level)
        )
      )
    `)
    .eq('user_id', userId)
    .eq('tenant_id', tenantId)
    .eq('status', 'approved') as any);

  if (error) {
    throw error;
  }

  return (data ?? []) as RoleAssignmentWithPermissions[];
}

function matchesLocationAtScope(
  assignment: RoleAssignmentWithPermissions,
  location: PermissionLocation | undefined,
  scopeLevel: ScopeLevel,
): boolean {
  const factoryId = location?.factoryId ?? null;
  const areaId = location?.areaId ?? null;
  const lineId = location?.lineId ?? null;
  const stationId = location?.stationId ?? null;
  const scopeRank = SCOPE_RANK[scopeLevel];

  if (scopeRank >= 1 && assignment.factory_id && (!factoryId || assignment.factory_id !== factoryId)) return false;
  if (scopeRank >= 2 && assignment.area_id && (!areaId || assignment.area_id !== areaId)) return false;
  if (scopeRank >= 3 && assignment.line_id && (!lineId || assignment.line_id !== lineId)) return false;
  if (scopeRank >= 4 && assignment.station_id && (!stationId || assignment.station_id !== stationId)) return false;

  return true;
}

export function checkPermissionFromAssignments(
  assignments: RoleAssignmentWithPermissions[],
  permission: AppPermission,
  location?: PermissionLocation,
): boolean {
  return assignments.some((assignment) => {
    const permissionDefinition = assignment.role.role_permissions.find(
      (rolePermission) => rolePermission.permission_definitions?.code === permission,
    )?.permission_definitions;

    if (!permissionDefinition) {
      return false;
    }

    return matchesLocationAtScope(
      assignment,
      location,
      permissionDefinition.applicable_scope_level ?? 'station',
    );
  });
}

export function extractAccessibleLineIds(
  assignments: RoleAssignmentWithPermissions[],
  lines: Array<{ id: string; factory_id: string; area_id: string }>,
): string[] {
  return lines
    .filter((line) => matchesLineAccess(assignments, {
      factoryId: line.factory_id,
      areaId: line.area_id,
      lineId: line.id,
    }))
    .map((line) => line.id);
}

export function matchesLineAccess(
  assignments: RoleAssignmentWithPermissions[],
  location?: PermissionLocation,
): boolean {
  return assignments.some((assignment) => assignment.role.role_permissions.some((rolePermission) => {
    const code = rolePermission.permission_definitions?.code;
    if (!code || !STYLING_PERMISSION_CODES.has(code)) {
      return false;
    }

    return matchesLocationAtScope(assignment, location, 'line');
  }));
}

export function getPermissionsFromAssignments(assignments: RoleAssignmentWithPermissions[]): Set<string> {
  const permissions = new Set<string>();

  assignments.forEach((assignment) => {
    assignment.role.role_permissions.forEach((rolePermission) => {
      const code = rolePermission.permission_definitions?.code;
      if (code) {
        permissions.add(code);
      }
    });
  });

  return permissions;
}

export function getStylingRolesForLocation(
  assignments: RoleAssignmentWithPermissions[],
  location?: PermissionLocation,
): StylingRole[] {
  return (Object.entries(STYLING_ROLE_PERMISSION_MAP) as Array<[StylingRole, AppPermission]>)
    .filter(([, permission]) => checkPermissionFromAssignments(assignments, permission, location))
    .map(([role]) => role);
}
